import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { Box } from '@leagueplatform/genesis-core';
import {
  useListItemsFocusHook,
  usePreventScroll,
} from '@leagueplatform/web-common';
import { debounce } from 'lodash-es';
import {
  DropdownMegaTooltip,
  DropdownMegaTooltipMobile,
  Overlay,
  BORDER_WIDTH as TOOLTIP_HEIGHT,
  DropdownListWrapper,
} from './user-dropdown.dropdown-styles';
import { DropdownList } from './user-dropdown.dropdown-list.component';
import type { UserDropdownProps } from './user-dropdown.types';

export const UserDropdown = ({
  isOpen,
  isMobile,
  setIsOpen,
  dropdownItemsConfig = [],
  getDropdownItemAnalyticsProps,
}: UserDropdownProps) => {
  const refsMenuItems: any[] = [];
  const refContainer = useRef<HTMLDivElement>();
  const [scrollableDropdown, setScrollableDropdown] = useState(false);
  useListItemsFocusHook(isOpen, refsMenuItems, refContainer);
  usePreventScroll(isMobile && isOpen);
  const formattedName = (action: string) =>
    action?.toLocaleLowerCase()?.replace(/_/g, ' ');

  const onDropdownClick = (action: string) => {
    setIsOpen(false);
    trackAnalyticsEvent(
      EVENT_NAME.BUTTON_CLICKED,
      getDropdownItemAnalyticsProps?.(formattedName(action)),
    );
  };

  // Represents for the diffrent heights used when determining if the dropdown should be scrollable. Extra margin space is included.
  const DROPDOWN_OFFSET = useMemo(
    () =>
      (refContainer?.current
        ? refContainer.current.getBoundingClientRect().top // Distance from the top of the screen.
        : 56 + TOOLTIP_HEIGHT) + 10,
    [refContainer],
  );

  const DROPDOWN_MAX_HEIGHT = useMemo(
    () => `calc(100vh - ${DROPDOWN_OFFSET}px)`,
    [DROPDOWN_OFFSET],
  );

  // Check if the dropdown is overflowing the screen, setting the scrollable dropdown state if neccessary.
  const checkDropdownOverflow = useCallback(() => {
    if (isOpen && refContainer?.current) {
      const overflowLimit = refContainer.current.offsetHeight + DROPDOWN_OFFSET;
      setScrollableDropdown(overflowLimit >= window.innerHeight);
    } else if (scrollableDropdown) {
      setScrollableDropdown(false);
    }
  }, [isOpen, refContainer, DROPDOWN_OFFSET, scrollableDropdown]);

  // Check dropdown overflow on mount, resize and when the dropdown is toggled.
  useEffect(() => {
    checkDropdownOverflow();

    // Adds a event delay, preventing it from firing too often.
    const debouncedCheckDropdownOverflow = debounce(checkDropdownOverflow, 100);
    window.addEventListener('resize', debouncedCheckDropdownOverflow);

    return () => {
      window.removeEventListener('resize', debouncedCheckDropdownOverflow);
      debouncedCheckDropdownOverflow.cancel();
    };
  }, [isOpen, refContainer, scrollableDropdown, checkDropdownOverflow]);

  // For Mobile Version
  if (isMobile) {
    return (
      <Box>
        {isOpen && <Overlay onClick={() => setIsOpen(false)} />}
        <DropdownMegaTooltipMobile
          isOpen={isOpen}
          id="user_dropdown_menu"
          role="dialog"
          ref={refContainer}
          hidden={!isOpen}
        >
          <DropdownListWrapper
            as="ul"
            isMobile={isMobile}
            css={{
              maxHeight: DROPDOWN_MAX_HEIGHT,
              ...(scrollableDropdown && { overflowY: 'scroll' }),
            }}
          >
            <DropdownList
              dropdownItemsConfig={dropdownItemsConfig}
              onDropdownClick={onDropdownClick}
              isMobile={isMobile}
              refsMenuItems={refsMenuItems}
            />
          </DropdownListWrapper>
        </DropdownMegaTooltipMobile>
      </Box>
    );
  }

  // For Desktop Version
  return (
    <DropdownMegaTooltip
      isOpen={isOpen}
      id="user_dropdown_menu"
      role="dialog"
      ref={refContainer}
      hidden={!isOpen}
    >
      <DropdownListWrapper
        as="ul"
        isMobile={isMobile}
        css={{
          maxHeight: DROPDOWN_MAX_HEIGHT,
          ...(scrollableDropdown && { overflowY: 'scroll' }),
        }}
      >
        <DropdownList
          dropdownItemsConfig={dropdownItemsConfig}
          onDropdownClick={onDropdownClick}
          isMobile={isMobile}
          refsMenuItems={refsMenuItems}
        />
      </DropdownListWrapper>
    </DropdownMegaTooltip>
  );
};
